import type { PaddleProduct } from '@/lib/paddle/checkout'
import {
  PACK_PRICING,
  RETAINER_AUDITS,
  RETAINER_PRICE_CENTS,
  formatDollars,
} from '@/lib/paddle/pricing'

/**
 * The Radar price ladder as rendered copy — ONE definition for every
 * marketing surface that quotes it (2026-09-04, heuristic finding F-01).
 *
 * Before this module, `/platform` held the ladder in a local `PACK_OPTIONS`
 * const with `$199` typed inline, and `/pricing` — the page the header's
 * "Pricing" link goes to — did not carry the Radar ladder at all. Any page
 * that wants to quote Radar's prices now reads them from here, and every
 * figure is DERIVED from `@/lib/paddle/pricing`, the checkout source of
 * truth, so a price change in Paddle's constants propagates instead of
 * drifting.
 *
 * Nothing here is a hardcoded currency figure. The only literals are copy.
 */

/** "$5" for whole dollars, "$4.50" when a price carries cents. */
function money(cents: number): string {
  return cents % 100 === 0 ? `$${cents / 100}` : formatDollars(cents)
}

const PACK_ORDER = ['pack-single', 'pack-starter', 'pack-power'] as const

/** Copy that has no numeric source — the unit noun and the card blurb. */
const PACK_COPY: Record<
  (typeof PACK_ORDER)[number],
  { unit: string; desc: string }
> = {
  'pack-single': {
    unit: 'single audit',
    desc: 'Try one complete audit across all 13 tools.',
  },
  'pack-starter': {
    unit: '3 audits',
    desc: 'Audit your site, a competitor, and your next release.',
  },
  'pack-power': {
    unit: '10 audits',
    desc: 'Best per-audit rate for active teams and agencies.',
  },
}

export interface RadarPackOption {
  count: number
  label: string
  price: string
  unit: string
  perAudit: string
  save: string
  desc: string
  product: PaddleProduct
}

// The single-audit rate is the reference the pack discounts are measured
// against, so "Save 20%" is computed rather than asserted.
const SINGLE_RATE_CENTS =
  PACK_PRICING['pack-single'].priceCents / PACK_PRICING['pack-single'].audits

export const RADAR_PACK_OPTIONS: RadarPackOption[] = PACK_ORDER.map(product => {
  const { priceCents, audits } = PACK_PRICING[product]
  const perAuditCents = priceCents / audits
  const savedPct = Math.round((1 - perAuditCents / SINGLE_RATE_CENTS) * 100)
  return {
    count: audits,
    label: `${audits} audit${audits === 1 ? '' : 's'}`,
    price: money(priceCents),
    unit: PACK_COPY[product].unit,
    perAudit: formatDollars(perAuditCents),
    save: savedPct > 0 ? `Save ${savedPct}%` : '',
    desc: PACK_COPY[product].desc,
    product,
  }
})

/** Cheapest way to unlock a full 13-tool audit — the "from $X" figure. */
export const RADAR_ENTRY_PRICE = RADAR_PACK_OPTIONS[0].price

/** The free tier is not a Paddle product, so $0 is stated, not derived. */
export const RADAR_FREE_PRICE = '$0'

export interface RadarTier {
  plan: string
  price: string
  unit: string
  note: string
  features: string[]
}

export const RADAR_FREE_TIER: RadarTier = {
  plan: 'Free',
  price: RADAR_FREE_PRICE,
  unit: 'forever',
  note: 'One technical readiness audit per domain. No card required.',
  features: [
    '6 technical readiness tools',
    'AI bot, robots.txt, and llms.txt checks',
    'Schema and AEO page audits',
    'AI Readiness score',
    '5 strategist messages',
  ],
}

export const RADAR_PACK_FEATURES: string[] = [
  'All 13 tools per audit',
  'Live citation, SOV, and hallucination checks',
  'Cross-tool insights and fix threads',
  'Unlimited strategist per audit',
  'PDF export and saved history',
  'Competitor comparison',
]

export const RADAR_RETAINER_TIER: RadarTier = {
  plan: 'Pro retainer',
  price: money(RETAINER_PRICE_CENTS),
  unit: '/ month',
  note: 'Ongoing visibility for client portfolios and in-house teams.',
  features: [
    `${RETAINER_AUDITS} full audits each month`,
    'All 13 tools per audit',
    'Watched domains',
    'Weekly pulse re-scans',
    'Unlimited strategist',
    'PDF export, history, and comparisons',
  ],
}
